import { useEffect, useState } from "react";
import { useFetcher } from "react-router";
import { $path } from "safe-routes";
import type { action as DistributionLinksEditAction } from "~/routes/account+/admin+/artists+/$artistId+/distribution-links";
import type { action as SocialLinksEditAction } from "~/routes/account+/admin+/artists+/$artistId+/social-links";
import { useLinks } from "./useLinks";

/**
 * Use this hook if you want a way to update the links for an artist
 * @returns
 */
export function useLinksEditor({
  artistUid,
  scope,
}: {
  artistUid: string;
  scope?: "social" | "distribution";
}) {
  const { links } = useLinks({ artistUid: artistUid, scope });

  const fetcher = useFetcher<
    typeof SocialLinksEditAction | typeof DistributionLinksEditAction
  >({
    key: "artist-links-action",
  });
  const isPending = fetcher.state !== "idle";

  const [resolvePromise, setResolvePromise] = useState<
    (() => void) | undefined
  >(undefined);

  useEffect(() => {
    if (fetcher.state === "idle" && resolvePromise) {
      resolvePromise();
      setResolvePromise(undefined);
    }
  }, [fetcher.state, resolvePromise]);

  function saveLinks(links: Array<string>, type: "social" | "distribution") {
    const form = new FormData();
    links.forEach((linkUid, index) => {
      form.append(`links[${index}]`, linkUid);
    });

    fetcher.submit(form, {
      action: $path(`/account/admin/artists/:artistId/${type}-links`, {
        artistId: artistUid,
      }),
      method: "POST",
    });
  }

  function saveLinksAsync(
    links: Array<string>,
    type: "social" | "distribution",
  ) {
    return new Promise((resolve) => {
      setResolvePromise(() => resolve);
      saveLinks(links, type);
    });
  }

  return {
    links,
    isPending,
    saveLinks,
    saveLinksAsync,
  };
}
